import * as Sentry from "@sentry/react";

/**
 * MonitoringService: Centralized monitoring and error reporting.
 * Acts as the principal architectural component for frontend observability.
 */
export const MonitoringService = {
    /**
     * Initializes Sentry for the frontend application.
     */
    init: () => {
        const dsn = import.meta.env.VITE_SENTRY_DSN;
        if (!dsn) {
            console.warn("Sentry DSN not found. Monitoring is disabled.");
            return;
        }

        Sentry.init({
            dsn,
            integrations: [],
            // Deactivate Performance Monitoring and Session Replay to stay within free tier
            tracesSampleRate: 0,
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 0,
            environment: import.meta.env.MODE,
        });

        // Initialize user if already in local storage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                MonitoringService.setUser(user);
            } catch (e) {
                // Ignore parse errors
            }
        }
    },

    /**
     * Updates the Sentry user context.
     */
    setUser: (user: any) => {
        if (!user) {
            Sentry.setUser(null);
            return;
        }
        Sentry.setUser({
            id: user.id || user.uid,
            email: user.email,
            username: user.fullName || user.displayName,
            salonId: user.salonId
        });
    },

    /**
     * Manually logs an exception to Sentry.
     */
    logException: (error: Error, extraInfo?: Record<string, any>) => {
        Sentry.withScope((scope) => {
            if (extraInfo) {
                scope.setExtras(extraInfo);
            }
            Sentry.captureException(error);
        });
    },

    /**
     * Manually tags the next errors with a specific context.
     */
    setTag: (key: string, value: string) => {
        Sentry.setTag(key, value);
    }
};

/**
 * Utility to manually trigger a test error for Sentry verification.
 * Used for debugging initialization and reporting.
 */
export const triggerSentryTest = () => {
    console.log("Triggering Sentry test error...");
    Sentry.captureException(new Error("Cresta Frontend Sentry Manual Test Error (Architectural Refactor)"));
};
